import { createClient } from '@/lib/supabase/client';

const supabase = createClient();
import { Database } from '@/types/database.types';
import { isValidUUID, validateUUID } from '@/lib/utils/validation';

type Article = Database['public']['Tables']['articles']['Row'];
type InsertArticle = Database['public']['Tables']['articles']['Insert'];
type UpdateArticle = Database['public']['Tables']['articles']['Update'];

// Filter and sort types
interface ArticleFilters {
  status?: string;
  category?: string;
  author?: string;
  search?: string;
}

interface ArticleSortOptions {
  field: 'createdAt' | 'publishDate' | 'title' | 'views' | 'seoScore';
  direction: 'asc' | 'desc';
}

export const articleService = {
  async getAllPublishedArticles() {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name, avatar_url),
        article_categories(name, color, slug)
      `)
      .eq('status', 'published')
      .order('published_date', { ascending: false });
    
    return { data, error };
  },

  // NEW: Get all articles with filters and sorting for admin management
  async getAll(filters?: ArticleFilters, sortOptions?: ArticleSortOptions) {
    try {
      let query = supabase
        .from('articles')
        .select(`
          *,
          author:user_profiles!articles_author_id_fkey(
            id,
            email,
            full_name,
            role,
            avatar_url
          ),
          category:article_categories!articles_category_id_fkey(
            id,
            name,
            slug,
            color
          )
        `);

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category_id', filters.category);
      }

      if (filters?.author && filters.author !== 'all') {
        query = query.eq('author_id', filters.author);
      }

      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,content.ilike.%${filters.search}%`);
      }

      // Apply sorting
      if (sortOptions) {
        const columnMap = {
          createdAt: 'created_at',
          publishDate: 'published_date',
          title: 'title',
          views: 'view_count',
          seoScore: 'seo_score'
        };

        query = query.order(columnMap[sortOptions.field], { 
          ascending: sortOptions.direction === 'asc' 
        });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform to match expected format
      const transformedData = data?.map(article => ({
        ...article,
        authorId: article.author_id,
        categoryId: article.category_id,
        featuredImageUrl: article.featured_image_url,
        featuredImageAlt: article.featured_image_alt,
        isFeatured: article.is_featured,
        publishedDate: article.published_date,
        scheduledDate: article.scheduled_date,
        viewCount: article.view_count,
        readTimeMinutes: article.read_time_minutes,
        seoScore: article.seo_score,
        metaDescription: article.meta_description,
        flaggedReason: article.flagged_reason,
        createdAt: article.created_at,
        updatedAt: article.updated_at
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // NEW: Create new article
  async create(articleData: {
    title: string;
    content: string;
    excerpt?: string;
    categoryId: string;
    status: string;
    featuredImageUrl?: string;
    featuredImageAlt?: string;
    metaDescription?: string;
    keywords?: string[];
    scheduledDate?: string;
    isFeatured: boolean;
  }, authorId: string) {
    try {
      // Validate UUIDs before database operation

      // Validate author ID
      validateUUID(authorId, 'author ID. Please ensure you are logged in properly');

      // Validate category ID if provided (convert empty string to null)
      const categoryId = articleData.categoryId?.trim() || null;
      if (categoryId && !isValidUUID(categoryId)) {
        throw new Error('Invalid category ID. Please select a valid category from the list.');
      }
      
      // Convert empty strings to null for optional UUID fields
      const scheduledDate = articleData.scheduledDate?.trim() || null;

      const { data, error } = await supabase
        .from('articles')
        .insert({
          title: articleData.title,
          content: articleData.content,
          excerpt: articleData.excerpt || '',
          category_id: categoryId,
          status: articleData.status as any,
          featured_image_url: articleData.featuredImageUrl || '',
          featured_image_alt: articleData.featuredImageAlt || '',
          meta_description: articleData.metaDescription || '',
          keywords: articleData.keywords || [],
          scheduled_date: scheduledDate,
          is_featured: articleData.isFeatured,
          author_id: authorId,
          published_date: articleData.status === 'published' ? new Date().toISOString() : null
        })
        .select(`
          *,
          author:user_profiles!articles_author_id_fkey(
            id,
            email,
            full_name,
            role,
            avatar_url
          ),
          category:article_categories!articles_category_id_fkey(
            id,
            name,
            slug,
            color
          )
        `)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // NEW: Update article
  async update(id: string, updates: Partial<UpdateArticle>, authorId: string) {
    try {
      // Validate UUIDs before database operation

      // Validate article ID
      validateUUID(id, 'article ID');

      // Validate author ID
      validateUUID(authorId, 'author ID. Please ensure you are logged in properly');

      // Validate category ID if being updated (convert empty string to null)
      if ('category_id' in updates) {
        const categoryId = updates.category_id?.toString().trim() || null;
        if (categoryId && !isValidUUID(categoryId)) {
          throw new Error('Invalid category ID. Please select a valid category from the list.');
        }
        updates.category_id = categoryId as any;
      }
      
      // Convert empty strings to null for optional UUID/timestamp fields
      if ('scheduled_date' in updates && typeof updates.scheduled_date === 'string') {
        updates.scheduled_date = updates.scheduled_date.trim() || null;
      }

      const { data, error } = await supabase
        .from('articles')
        .update(updates)
        .eq('id', id)
        .eq('author_id', authorId)
        .select(`
          *,
          author:user_profiles!articles_author_id_fkey(
            id,
            email,
            full_name,
            role,
            avatar_url
          ),
          category:article_categories!articles_category_id_fkey(
            id,
            name,
            slug,
            color
          )
        `)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // NEW: Delete article
  async delete(id: string, authorId: string) {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id)
        .eq('author_id', authorId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // NEW: Bulk update status
  async bulkUpdateStatus(articleIds: string[], status: string, authorId: string) {
    try {
      const updates: Partial<UpdateArticle> = { 
        status: status as any,
        published_date: status === 'published' ? new Date().toISOString() : undefined
      };

      const { error } = await supabase
        .from('articles')
        .update(updates)
        .in('id', articleIds)
        .eq('author_id', authorId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // NEW: Bulk delete articles
  async bulkDelete(articleIds: string[], authorId: string) {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .in('id', articleIds)
        .eq('author_id', authorId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // NEW: Get article by ID (for editing)
  async getById(id: string) {
    try {
      // Validate UUID format before querying database

      if (!isValidUUID(id)) {
        // Return friendly error for invalid UUID format (like mock IDs)
        return {
          data: null,
          error: new Error('Invalid article ID format. Please ensure you are using a valid article from the database.')
        };
      }

      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          author:user_profiles!articles_author_id_fkey(
            id,
            email,
            full_name,
            role,
            avatar_url
          ),
          category:article_categories!articles_category_id_fkey(
            id,
            name,
            slug,
            color
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Transform to match expected format
      if (data) {
        const transformedData = {
          ...data,
          authorId: data.author_id,
          categoryId: data.category_id,
          featuredImageUrl: data.featured_image_url,
          featuredImageAlt: data.featured_image_alt,
          isFeatured: data.is_featured,
          publishedDate: data.published_date,
          scheduledDate: data.scheduled_date,
          viewCount: data.view_count,
          readTimeMinutes: data.read_time_minutes,
          seoScore: data.seo_score,
          metaDescription: data.meta_description,
          flaggedReason: data.flagged_reason,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };

        return { data: transformedData, error: null };
      }

      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getArticleBySlug(slug: string) {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name, avatar_url, bio),
        article_categories(name, color, slug)
      `)
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (!error && data) {
      await this.incrementViews(data.id);
    }

    return { data, error };
  },

  async getRelatedArticles(categoryId: string, excludeArticleId: string, limit: number = 3) {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name, avatar_url),
        article_categories(name, color, slug)
      `)
      .eq('status', 'published')
      .eq('category_id', categoryId)
      .neq('id', excludeArticleId)
      .order('published_date', { ascending: false })
      .limit(limit);

    return { data, error };
  },

  async getFeaturedArticles() {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name),
        article_categories(name, color)
      `)
      .eq('status', 'published')
      .eq('is_featured', true)
      .limit(5);
    
    return { data, error };
  },

  async getFeatured(limit: number = 5) {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name, avatar_url),
        article_categories(name, color)
      `)
      .eq('status', 'published')
      .eq('is_featured', true)
      .order('published_date', { ascending: false })
      .limit(limit);
    
    if (error) {
      return [];
    }
    return data || [];
  },

  async getArticlesByCategory(categoryId: string) {
    const { data, error } = await supabase
      .from('articles')
      .select(`
        *,
        user_profiles!articles_author_id_fkey(full_name, avatar_url),
        article_categories(name, color)
      `)
      .eq('status', 'published')
      .eq('category_id', categoryId)
      .order('published_date', { ascending: false });
    
    return { data, error };
  },

  async getAllArticles() {
    const { data, error } = await supabase
      .from('articles')
      .select('id, status, updated_at, created_at')
      .eq('status', 'published')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async createArticle(article: InsertArticle) {
    const { data, error } = await supabase
      .from('articles')
      .insert(article)
      .select()
      .single();
    
    return { data, error };
  },

  async updateArticle(id: string, updates: UpdateArticle, authorId: string) {
    const { data, error } = await supabase
      .from('articles')
      .update(updates)
      .eq('id', id)
      .eq('author_id', authorId)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteArticle(id: string, authorId: string) {
    const { error } = await supabase
      .from('articles')
      .delete()
      .eq('id', id)
      .eq('author_id', authorId);
    
    return { error };
  },

  // NEW: Update article status (for bulk actions)
  async updateStatus(articleId: string, status: string) {
    try {
      const { error } = await supabase
        .from('articles')
        .update({ 
          status: status as any,
          published_date: status === 'published' ? new Date().toISOString() : null
        })
        .eq('id', articleId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // NEW: Delete article (simplified version for management page)
  async delete(articleId: string) {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  async getCategories() {
    const { data, error } = await supabase
      .from('article_categories')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });
    
    return { data, error };
  },

  async incrementViews(articleId: string) {
    const { error } = await supabase
      .rpc('increment_article_views', { article_uuid: articleId });

    return { error };
  },
};